import type { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { openSignupGateImperative } from '@/store/authModalStore';

/** Marks a request already replayed after completion, so a second 403 can't loop. */
const SIGNUP_RETRY_FLAG = '__signupRetry__';

function isSignupRetry(config: AxiosRequestConfig | undefined): boolean {
  return Boolean(config && (config as Record<string, unknown>)[SIGNUP_RETRY_FLAG]);
}

interface SignupIncompleteBody {
  code?: string;
}

/**
 * Backstop for the server's signup-completion gate. The api rejects every
 * authenticated mutating request from a mid-signup account (email present but
 * unverified) with 403 `{ code: 'SIGNUP_INCOMPLETE' }` by default — see
 * AuthenticationGuard in predictions-api. The proactive client gate
 * (`useOnboardingGate`, used by every gated surface) gives the nice UX;
 * this interceptor guarantees the behavior for any surface that forgot it:
 * the funnel opens at the user's next step, and on completion the original
 * request is replayed transparently (mirrors the KYC_REQUIRED interceptor).
 * If the user dismisses the funnel, the original 403 propagates.
 *
 * Returns a teardown function (useful for tests / HMR).
 */
export function installSignupInterceptor(instance: AxiosInstance): () => void {
  const id = instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const body = error.response?.data as SignupIncompleteBody | undefined;
      const config = error.config;
      if (error.response?.status === 403 && body?.code === 'SIGNUP_INCOMPLETE' && config && !isSignupRetry(config)) {
        const outcome = await openSignupGateImperative();
        if (outcome === 'completed') {
          return instance.request({ ...config, [SIGNUP_RETRY_FLAG]: true } as AxiosRequestConfig);
        }
      }
      throw error;
    },
  );
  return () => instance.interceptors.response.eject(id);
}
